import { QueryFunctionContext } from '@tanstack/react-query';
import { getArtifactsResults } from 'api/platform';
import { ModelInference, OrchestrationInference, ResultNameOrchestrations } from 'interfaces/calculateFeatures';
import { ArtifactResultsConfig } from 'interfaces/inferenceArtifacts';
import { getArtifactResultsErrorMessage } from 'utils/artifactValidationError';
import { CasesParams } from 'utils/useCasesParams/types';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';

export const isWaitingForArtifactResults = ({
  enabled = true,
  isFetched,
  isFetching,
  isLoading,
  isPlaceholderData,
}: {
  enabled?: boolean;
  isFetched: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isPlaceholderData: boolean;
}) => Boolean(enabled) && (isLoading || isFetching || isPlaceholderData || !isFetched);

export const useArtifactResultsQuery = <
  T extends ModelInference | OrchestrationInference | ResultNameOrchestrations = ModelInference
>({
  caseParams,
  config,
  enabled = true,
}: {
  caseParams: CasesParams;
  config: ArtifactResultsConfig;
  enabled?: boolean;
}) => {
  const inferenceArtifactsParams = {
    ...caseParams,
    configParams: config,
  };
  const query = useQueryWithErrorAndRetrySnackbar(
    ['artifactsResults', inferenceArtifactsParams],
    ({ signal }: QueryFunctionContext) => getArtifactsResults<T>(inferenceArtifactsParams, signal),
    {
      enabled,
      errorMessageGetter: getArtifactResultsErrorMessage,
      placeholderData: [] as T[],
      queriedEntityName: 'artifact inputs',
      retry: false,
    }
  );

  return {
    ...query,
    isWaitingForResults: isWaitingForArtifactResults({
      enabled,
      isFetched: query.isFetched,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isPlaceholderData: query.isPlaceholderData,
    }),
  };
};
